import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import useStyles from 'isomorphic-style-loader/useStyles';
import Menu, { SubMenu } from 'rc-menu';
import { toast } from 'react-toastify';

// MaterialUI
import ListItemText from '@material-ui/core/ListItemText';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import VisibilityIcon from '@material-ui/icons/VisibilityOutlined';

// app modules: actions
import updateCollection from 'actions/user/updateCollection';

// app modules: props
import CollectionsProps from 'components/Collection/CollectionsProps';

// app modules: constants
import { PRIVACY_OPTIONS } from 'constants/privacy';

// assets
import CssOptionsMenu from './OptionsMenu.scss';

function PrivacyMenuItem(props) {
  const { ItemComponent, Icon, value, label, secondary, collection, onClick, ...others } = props;
  useStyles(CssOptionsMenu);
  const dispatch = useDispatch();

  const handleClick = async () => {
    const promise = dispatch(updateCollection(collection.id, { visibility: value }));
    setTimeout(onClick, 300);
    const { error, data } = await promise;
    if (error) {
      if (navigator.onLine) {
        toast.error(<p>Cannot change collection privacy right now. Please try again later.</p>);
      } else {
        toast.error(<p>Cannot change collection privacy while offline</p>);
      }
    } else {
      toast.success(
        <p>
          Collection privacy status is now{' '}
          <b>{data.visibility.charAt(0).toUpperCase() + data.visibility.slice(1)}</b>
        </p>,
      );
    }
  };
  return (
    <ItemComponent
      key={value}
      value={value}
      selected={value === collection.visibility}
      className={value === collection.visibility ? 'options-menu__item--selected' : ''}
      {...others}
      onClick={handleClick}
    >
      {Icon ? (
        <ListItemIcon>
          <Icon fontSize="large" color={value === collection.visibility ? 'primary' : ''} />
        </ListItemIcon>
      ) : null}
      <ListItemText secondary={secondary}>{label}</ListItemText>
    </ItemComponent>
  );
}

PrivacyMenuItem.propTypes = {
  ItemComponent: PropTypes.elementType.isRequired,
  Icon: PropTypes.elementType,
  value: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  secondary: PropTypes.string,
  collection: CollectionsProps.isRequired,
  onClick: PropTypes.func,
};

PrivacyMenuItem.defaultProps = {
  Icon: null,
  secondary: null,
  onClick: () => {},
};

export function PrivacyMenuItems(props) {
  return PRIVACY_OPTIONS.map(option => <PrivacyMenuItem {...props} {...option} />);
}

export function ManagePrivacyMenu(props) {
  return (
    <Menu selectable={false}>
      {PRIVACY_OPTIONS.map(option => <PrivacyMenuItem {...props} {...option} />)}
    </Menu>
  );
}

function ManagePrivacyItem(props) {
  const { ItemComponent, collection, popup, onClick, onSubMenuItemClick, ...others } = props;
  useStyles(CssOptionsMenu);

  if (collection.can_edit) {
    const content = (
      <React.Fragment>
        <ListItemIcon>
          <VisibilityIcon color="primary" fontSize="large" />
        </ListItemIcon>
        <ListItemText primary="Manage privacy" />
      </React.Fragment>
    );
    if (popup) {
      return (
        <SubMenu className="options-menu__item" popupOffset={[-5, 0]} title={content} {...others}>
          {PRIVACY_OPTIONS.map(option => (
            <PrivacyMenuItem {...props} {...option} onClick={onSubMenuItemClick} />
          ))}
        </SubMenu>
      );
    }
    return (
      <ItemComponent className="options-menu__item" button onClick={onClick}>
        {content}
      </ItemComponent>
    );
  }
  return null;
}

ManagePrivacyItem.propTypes = {
  ItemComponent: PropTypes.elementType.isRequired,
  collection: CollectionsProps.isRequired,
  popup: PropTypes.bool,
  onClick: PropTypes.func,
  onSubMenuItemClick: PropTypes.func,
};

ManagePrivacyItem.defaultProps = {
  popup: false,
  onClick: () => {},
  onSubMenuItemClick: () => {},
};

export default ManagePrivacyItem;
